var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { TimeoutError, Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
export class NecProjector {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.codeOffset = 0x30;
        this.controlId = 0;
        this.isChangingVideoInput = false;
        this.modelCode = 0;
        this.mostlyReady = true;
        this.powerOnOffProcess = false;
        this.productName = '';
        this.timeouts = new Timeouts();
        this.verbose = true;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.displayBlank.value;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x02, blank ? 0x10 : 0x11, 0x00, 0x00, 0x00]), /(\x22)[\x10\x11][\s\S\]{2}\x00[\s\S]|\xA2[\x10\x11][\s\S]{2}\x02[\s\S]{3}/, tags);
                return match[1] !== undefined ? blank : this.state.displayBlank.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.displayFreeze = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.displayFreeze.value;
            }),
            set: (freeze, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x98, 0x00, 0x00, 0x01, freeze ? 0x01 : 0x02]), /(\x21)\x98[\s\S\]{2}\x01[\s\S]{2}|\xA1\x98[\s\S]{2}\x02[\s\S]{3}/, tags);
                return match[1] !== undefined ? freeze : this.state.displayFreeze.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.powerOnOffProcess || this.pendingPower !== undefined) {
                    this.pendingPower = power;
                    this.state.busy.update(this.computeBusy());
                    return power;
                }
                let suspendMs = 0;
                if (this.socket.port === 4999) {
                    // When powering down via RS-232, the projector stops responding to commands for up
                    // to 13 seconds. When powering up, the running status command can take up to 10
                    // seconds to reflect accurate values.
                    suspendMs = power ? 30000 : 20000;
                }
                else {
                    // When powering down via Ethernet, the projector works fine for a while, but
                    // eventually drops the connection (80 seconds with the NP-M322W). Instead of
                    // calling reconnect here for the power off case, we have code in the poll functions
                    // that checks for command timeouts and reconnects if we get one.
                    suspendMs = power ? 30000 : 20000;
                }
                // In all cases, the running status command (used for polls) can take a while to reflect
                // the correct values.
                this.framework.suspendPolling(suspendMs);
                // Manually update without waiting for poll.
                this.powerOnOffProcess = true;
                this.mostlyReady = false;
                this.state.busy.update(this.computeBusy());
                const match = yield this.socket.send(this.makeCommand([0x02, power ? 0x00 : 0x01, this.controlId, this.modelCode, 0x00]), /\x22([\s\S])[\s\S]{2}\x00[\s\S]|\xA2([\s\S])[\s\S]{2}\x02([\s\S])([\s\S])[\s\S]/, tags);
                if (match[1] === undefined)
                    return this.state.power.value;
                this.state.power.update(power);
                // Wait for the projector to become responsive before processing any more commands.
                yield this.timeouts.delay(suspendMs);
                return this.state.power.value;
            }),
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const match = yield this.socket.send(this.makeCommand([0x00, 0xbf, this.controlId, this.modelCode, 0x02, 0x01, 0x06]), /\x20\xbf[\s\S]{2}\x12\x01\x06([\s\S]{16})[\s\S]|\xA0\xBF[\s\S]{2}\x02[\s\S]{2}[\s\S]/, tags);
                return match[1] !== undefined
                    ? match[1].replace(/\x00/g, '')
                    : (_a = this.state.serialNumber.value) !== null && _a !== void 0 ? _a : 'unknown';
            }),
            powerOffRestrictions: ['get'],
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x00, 0x85, this.controlId, this.modelCode, 0x01, 0x02]), /\x20\x85[\s\S]{2}\x10([\s\S]{16})[\s\S]|\xA0\x85[\s\S]{2}\x02([\s\S])([\s\S])[\s\S]/, tags);
                if (match[1] === undefined)
                    return this.state.videoInput.value;
                const status = match[1];
                //const data01 = status.charCodeAt(0); // data 01: signal switch process
                const data02 = status.charCodeAt(1); // data 02: signal list number
                const data03 = status.charCodeAt(2); // data 03: selection signal type 1
                const data04 = status.charCodeAt(3); // data 04: selection signal type 2
                //const signalListType = status.charCodeAt(4); // data 05: signal list type
                //const testPatternDisplay = status.charCodeAt(5); // data 06: test pattern display
                //const contentDisplayed = status.charCodeAt(8); // data 09: content displayed
                if (data02 !== 0xff) {
                    // 0xFF means the input code information is not valid, so we try to recreate the
                    // input from the other data in data 03 and 04. Note: the projector returns the
                    // signal number - 1.
                    return String.fromCharCode(data02 + 1 + this.codeOffset);
                }
                let inputSourceCode = 0x01;
                // data03 (selection signal type 1) tells us the port type. For example, 1 or 2 for HDMI.
                // Use data04 (selection signal type 2) to determine the signal type.
                //
                // The NEC P554U reports the following information:
                //
                // data      02 03 04
                //
                // HDMI      FF 01 21
                // HDMI2     FF 02 21
                // Computer  FF 01 01
                // Video     FF 01 02
                // HDBAseT   FF 01 27
                // USB A     FF 02 07
                switch (data04) {
                    case 0x01: // Computer
                        if (data03 === 1) {
                            inputSourceCode = 0x01;
                        }
                        else if (data03 === 2) {
                            inputSourceCode = 0x02;
                        }
                        else if (data03 === 3) {
                            inputSourceCode = 0x03;
                        }
                        break;
                    case 0x02: // Video
                        inputSourceCode = 0x06;
                        break;
                    case 0x03: // S-Video
                        inputSourceCode = 0x0b;
                        break;
                    case 0x04: // Component
                        inputSourceCode = 0x10;
                        break;
                    case 0x06: // Not documented, but this is HDMI 1 and 2 on my projector.
                        inputSourceCode = data03 === 1 ? 0x1a : 0xa2;
                        break;
                    case 0x07: // Viewer
                        inputSourceCode = 0x1f;
                        break;
                    case 0x20: // DVI-D
                        inputSourceCode = 0x1b;
                        break;
                    case 0x21: // HDMI
                        if (data03 === 1) {
                            inputSourceCode = 0x1a;
                        }
                        else if (data03 === 2) {
                            inputSourceCode = 0xa2;
                        }
                        break;
                    case 0x23: // Viewer
                        inputSourceCode = 0x1f;
                        break;
                    case 0x27: // Viewer
                        inputSourceCode = 0x20;
                        break;
                    default:
                        break;
                }
                return String.fromCharCode(inputSourceCode + this.codeOffset);
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                if (!this.mostlyReady) {
                    // If the projector is still powering on or off, ignore the command.
                    return this.state.videoInput.value;
                }
                let sendCode = code.charCodeAt(0) - this.codeOffset;
                // Some models have different codes that need to be sent.
                switch (sendCode) {
                    case 0x1a: // HDMI
                        if ([
                            'P554U',
                            'P554W',
                            'P474U',
                            'P474W',
                            'P604X',
                            'P502H',
                            'P502HL',
                            'P502WL',
                            'P502W',
                            'P452H',
                            'P452W',
                            'PA803UL',
                            'PA653UL',
                            'PA803U',
                            'PA723U',
                            'PA653U',
                            'PA853W',
                            'PA703W',
                            'PA903X',
                            'M403X',
                            'M363X',
                            'M323X',
                            'M283X',
                            'M403W',
                            'M363W',
                            'M323W',
                            'M333XS',
                            'M353WS',
                            'M303WS',
                            'M403H',
                            'M323H',
                            'M353HS',
                            'M323HS',
                            'M303HS',
                        ].includes(this.productName)) {
                            sendCode = 0xa1;
                        }
                        break;
                    case 0x20: // HDBASE-T
                        if ([
                            'P554U',
                            'P554W',
                            'P474U',
                            'P474W',
                            'P604X',
                            'P502H',
                            'P502HL',
                            'P502WL',
                            'P502W',
                            'P452H',
                            'P452W',
                            'PA803UL',
                            'PA653UL',
                            'PA803U',
                            'PA723U',
                            'PA653U',
                            'PA853W',
                            'PA703W',
                            'PA903X',
                        ].includes(this.productName)) {
                            sendCode = 0xbf;
                        }
                        break;
                    case 0x03: // Computer 3
                        if (['PX651X', 'PX581W'].includes(this.productName)) {
                            sendCode = 0x9c;
                        }
                        else if ([
                            'PX620X',
                            'PX550W',
                            'NP4100',
                            'NP4100W',
                            'M420X',
                            'M420XV',
                            'M350X',
                            'M300X',
                            'M260X',
                            'M230X',
                            'M300W',
                            'M350X',
                            'M300X',
                            'M260X',
                            'M230X',
                            'M300W',
                            'M260W',
                            'M350XS',
                            'M300XS',
                            'M260XS',
                            'M300WS',
                            'M260WS',
                        ].includes(this.productName)) {
                            sendCode = 0x1a;
                        }
                        break;
                    case 0xa2: // HDMI2
                        if (['UM330X', 'UM280X', 'UM330W', 'UM280W'].includes(this.productName)) {
                            sendCode = 0x1b;
                        }
                        break;
                    case 0xa6: // Display port
                        if ([
                            'PA600X',
                            'PA500X',
                            'PA550W',
                            'PA500U',
                            'PH1400U',
                            'PH1000U',
                            'PX750U',
                            'PX700W',
                            'PX800X',
                        ].includes(this.productName)) {
                            sendCode = 0x1b;
                        }
                        break;
                    default:
                        break;
                }
                this.isChangingVideoInput = true;
                this.state.busy.update(this.computeBusy());
                const match = yield this.socket.send(this.makeCommand([
                    0x02,
                    0x03,
                    this.controlId,
                    this.modelCode,
                    0x02,
                    0x01,
                    sendCode,
                ]), /\x22\x03[\s\S]{2}\x01([\s\S])[\s\S]|\xA2\x03[\s\S]{2}\x02([\s\S])([\s\S])[\s\S]/, tags);
                if (match[1] === undefined || match[1].charCodeAt(0) !== 0) {
                    this.isChangingVideoInput = false;
                    this.state.busy.update(this.computeBusy());
                    return this.state.videoInput.value;
                }
                // Wait for the projector to finish switching the video input.
                const startTimestamp = Date.now();
                while (true) {
                    if (Date.now() - startTimestamp > 10000) {
                        this.log.warning(`timeout waiting for signal to switch`);
                        break;
                    }
                    const basicInfo = yield this.getBasicInformation();
                    this.log.debug(`signalBeingSwitched = ${basicInfo === null || basicInfo === void 0 ? void 0 : basicInfo.signalBeingSwitched}`);
                    if (Date.now() - startTimestamp > 3000 &&
                        basicInfo !== undefined &&
                        !basicInfo.signalBeingSwitched) {
                        break;
                    }
                    yield this.timeouts.delay(1000);
                }
                this.isChangingVideoInput = false;
                this.state.busy.update(this.computeBusy());
                return code;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            range: { max: 0x1f, min: 0, round: 1 },
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x03,
                    0x05,
                    this.controlId,
                    this.modelCode,
                    0x03,
                    0x05,
                    0x00,
                    0x00,
                ]), /\x23\x05[\s\S]{2}\x10[\s\S]{7}([\s\S]{2})[\s\S]{7}[\s\S]|\xA3\x05[\s\S]{2}\x02[\s\S]{2}[\s\S]/, tags);
                if (match[1] === undefined)
                    return this.state.volume.value;
                return (match[1].charCodeAt(1) << 8) + match[1].charCodeAt(0);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                if (!this.mostlyReady) {
                    // If the projector is still powering on or off, ignore the command.
                    return this.state.volume.value;
                }
                const match = yield this.socket.send(this.makeCommand([
                    0x03,
                    0x10,
                    this.controlId,
                    this.modelCode,
                    0x05,
                    0x05,
                    0x00,
                    0x00,
                    volume & 0xff,
                    (volume >> 8) & 0xff,
                ]), /\x23\x10[\s\S]{2}\x02([\s\S]{2})[\s\S]|\xA3\x10[\s\S]{2}\x02([\s\S])([\s\S])[\s\S]/, tags);
                return match[1] !== undefined && match[1].charCodeAt(0) + match[1].charCodeAt(1) === 0
                    ? volume
                    : this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                if (!this.mostlyReady) {
                    // If the projector is still powering on or off, ignore the command.
                    return this.state.volumeMute.value;
                }
                const match = yield this.socket.send(this.makeCommand([0x02, mute ? 0x12 : 0x13, this.controlId, this.modelCode, 0x00]), /\x22([\x12\x13])[\s\S]{2}\x00[\s\S]|\xA2[\x12\x13][\s\S]{2}\x02([\s\S]{2})[\s\S]/, tags);
                return match[1] !== undefined
                    ? match[1].charCodeAt(0) === 0x12
                    : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    try {
                        this.state.videoInput.update(yield this.videoInput.get(['get.videoInput', 'poll']));
                        yield this.getRunningStatus();
                        yield this.getBasicInformation();
                    }
                    catch (error) {
                        if (this.powerOnOffProcess &&
                            error instanceof TransportError &&
                            error.code === 'sendTimeout') {
                            yield this.framework.reconnect({
                                initialDelayMs: 0,
                                numAttempts: 3,
                                attemptDelayMs: 2000,
                            });
                        }
                        else if (!(error instanceof TimeoutError) ||
                            error.code !== 'timeoutCleared') {
                            throw error;
                        }
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.isChangingVideoInput = false;
            this.mostlyReady = true;
            this.pendingPower = undefined;
            this.powerOnOffProcess = false;
            yield this.getRunningStatus(['configuring']); // update busy, power, and powerOnOffProcess
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const initializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: '\x00\x88\x00\x00\x00\x88',
                    next: /\x20\x88[\s\S]{2}\x0C[\s\S]{12}[\s\S]|\xA0\x88[\s\S]{2}\x02[\s\S]{3}|\xA2\x00\x00[\s\S]{5}/,
                    // A2 00 00 20 02 00 00 C4 is returned from MSRC - Houston's projector
                },
            ];
            const result = yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
            if (result === 'connected') {
                // Get product name.
                const match = yield this.socket.send(this.makeCommand([0x00, 0x85, this.controlId, this.modelCode, 0x01, 0x04]), /\x20\x85[\s\S]{2}\x20([\s\S]{32})[\s\S]|\xA0\x85[\s\S]{2}\x02[\s\S]{2}[\s\S]|\xA2\x00\x00[\s\S]{5}/, ['configuring']);
                this.productName = (_b = (_a = match[1]) === null || _a === void 0 ? void 0 : _a.replace(/\x00/g, '')) !== null && _b !== void 0 ? _b : '';
            }
            return result;
        });
    }
    // other class methods
    computeBusy() {
        return !this.mostlyReady || this.pendingPower !== undefined || this.isChangingVideoInput;
    }
    getBasicInformation(tags = ['poll']) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send(this.makeCommand([0x00, 0xbf, this.controlId, this.modelCode, 0x01, 0x02]), /\x20\xBF[\s\S]{2}\x10\x02([\s\S]{9})[\s\S]{7}|\xA0\xBF[\s\S]{6}/, [...tags, 'basicInformation.get']);
            if (!match[1] || this.framework.isPollingSuspended)
                return undefined;
            const status = match[1];
            // const operationStatus = status.charCodeAt(0); // data01: handled in getRunningStatus
            const contentDisplayed = status.charCodeAt(1); // data02
            // const selectionSignalType1 = status.charCodeAt(2); // data03
            // const selectionSignalType2 = status.charCodeAt(3); // data04
            // const displaySignalType = status.charCodeAt(4); // data05
            const videoMute = status.charCodeAt(5); // data06
            const soundMute = status.charCodeAt(6); // data07
            // const onscreenMute = status.charCodeAt(7); // data08
            const freezeStatus = status.charCodeAt(8); // data09
            this.state.displayBlank.update(videoMute === 1);
            this.state.displayFreeze.update(freezeStatus === 1);
            this.state.volumeMute.update(soundMute === 1);
            return {
                signalBeingSwitched: (contentDisplayed & 0x10) > 0,
            };
        });
    }
    getRunningStatus(tags = ['poll']) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send(this.makeCommand([0x00, 0x85, this.controlId, this.modelCode, 0x01, 0x01]), /\x20\x85[\s\S]{2}\x10([\s\S]{16})[\s\S]|\xA0\x85[\s\S]{2}\x02([\s\S])([\s\S])[\s\S]|\xA2\x00\x00[\s\S]{5}/, [...tags, 'runningStatus.get']);
            if (match[1] === undefined || this.framework.isPollingSuspended)
                return;
            const status = match[1];
            // fields:
            //
            // data 06: operation status:
            //     - 00: standby
            //     - 03: seen but not documented
            //     - 04: power on
            //     - 05: cooling
            //     - 06: standby (error)
            //     - 0F: standby (power saving)
            //     - 10: network standby
            //     - ff: not supported
            //
            // Power on observation:
            //
            // | Condition      | time (sec) | power | cooling | powerOnOffProcess | operation |
            // | -------------- | ---------: | ----: | ------: | ----------------: | --------: |
            // | off            |          0 |     0 |       0 |                 0 |         0 |
            // | on command     |          0 |       |         |                   |           |
            // | ...            |          6 |     0 |       0 |                 1 |         2 |
            // | ...            |         14 |     0 |       0 |                 1 |         3 |
            // | ...            |         18 |     1 |       0 |                 1 |         4 |
            // | complete (on)  |         74 |     1 |       0 |                 0 |         4 |
            //
            // Power off observation:
            //
            // | Condition      | time (sec) | power | cooling | powerOnOffProcess | operation |
            // | -------------- | ---------: | ----: | ------: | ----------------: | --------: |
            // | on             |          0 |     0 |       0 |                 0 |         0 |
            // | off command    |          0 |       |         |                   |           |
            // | ...            |         13 |     0 |       1 |                 1 |         5 |
            // | timeouts       |         79 |     0 |       1 |                 1 |         5 |
            // | reconnected    |         98 |       |         |                   |           |
            // | complete (off) |        102 |     0 |       0 |                 0 |         0 |
            // | network standby| much later |     0 |       0 |                 0 |        16 |
            // Operation status will be 16 for network standby or 15 for standby (power saving).
            const powerStatus = status.charCodeAt(2); // data 03
            const coolingProcess = status.charCodeAt(3); // data 04
            const powerOnOffProcess = status.charCodeAt(4); // data 05
            const operationStatus = status.charCodeAt(5); // data 06
            // From observing the status bytes during start up and power down:
            //
            //                      data bytes
            //       state          03 04 05 06
            //       ------         -----------
            //      power off        0  0  0  0
            //
            // Turn on
            //      start power up   0  0  1  1
            //           ...
            //      start power up   0  0  1  1
            //      finishing power  0  0  1  2
            //                       1  0  1  4
            //
            //      power on         1  0  0  4    Fully turned on, and can be turned off
            //
            // Turn off
            //      start power down 0  1  1  5
            //          ...
            //      start power down 0  1  1  5
            //
            //      power off        0  0  0  0    Fully turned off, and can be turned on
            // When power on is in process, operationStatus can return a value of 2 which is not defined
            // in the NEC API guide.
            this.mostlyReady =
                (powerOnOffProcess !== 1 && operationStatus === 0) ||
                    operationStatus === 4 ||
                    operationStatus === 16;
            this.powerOnOffProcess = powerOnOffProcess === 1;
            this.state.busy.update(this.computeBusy());
            if (this.verbose) {
                this.log.debug(`getRunningStatus: powerStatus = ${powerStatus}`);
                this.log.debug(`getRunningStatus: coolingProcess = ${coolingProcess}`);
                this.log.debug(`getRunningStatus: powerOnOffProcess = ${powerOnOffProcess}`);
                this.log.debug(`getRunningStatus: operationStatus = ${operationStatus}`);
                this.log.debug(`getRunningStatus: this.mostlyReady = ${this.mostlyReady}`);
                this.log.debug(`getRunningStatus: this.pendingPower = ${this.pendingPower}`);
            }
            // Note: this.pendingPower may be undefined after reconnecting to the projector
            if (this.mostlyReady && this.pendingPower === undefined) {
                this.state.power.update(powerStatus === 1);
            }
            if (this.pendingPower !== undefined && !this.powerOnOffProcess) {
                const newPower = this.pendingPower;
                this.pendingPower = undefined;
                this.power.set(newPower, ['power.set']); // don't await
            }
        });
    }
    makeCommand(data) {
        const bytes = [...data];
        const checksum = bytes.reduce((sum, current) => sum + current);
        bytes.push(checksum & 0x00ff);
        return AsciiText.decode(bytes);
    }
}
// static properties
NecProjector.driverType = 'socketDriver';
NecProjector.key = '-LRMc4JVp7tCiIQfQAQ5';
NecProjector.baseSocketOptions = {
    commandExtractor: (buffer) => {
        // Command Format:
        //       0          1        2     3        4        5 - 41            42
        //   [command] [command2] [ID1] [ID2] [DataLength] [data: 0-36 bytes] [checksum]
        //
        // There can be more than 36 bytes of data.
        //
        // The checksum is lower 8-bits of the sum of all the bytes in the command.
        //
        // Acknowledgments are at least 6 bytes long (i.e., when the data length is 0) and can
        // be longer.
        let i = 0;
        const startBytes = [
            String.fromCharCode(0x02),
            String.fromCharCode(0x20),
            String.fromCharCode(0x21),
            String.fromCharCode(0x22),
            String.fromCharCode(0x23),
            String.fromCharCode(0xa0),
            String.fromCharCode(0xa1),
            String.fromCharCode(0xa2),
            String.fromCharCode(0xa3),
        ];
        // Skip over any garbage characters at the beginning of the command.
        while (!startBytes.includes(buffer[i]) && i < buffer.length)
            i++;
        // Make sure we have enough data to compute the command length.
        if (buffer.length - i < 6)
            return undefined;
        // 5 comes from getting past the header bytes 0,1,2,3,4 + the length + the checksum
        const numCmdBytes = 5 + buffer.charCodeAt(i + 4) + 1;
        // Make sure we have the full command.
        if (buffer.length - i < numCmdBytes)
            return undefined;
        // When the PN502HL2 is powered off, the last 6 bytes of the command include the start
        // of a new comand, rather than the remaining 0's in the response and the checksum.
        const pn502WorkAround = buffer.charCodeAt(i + 1) === 0x85 && // command number for name
            numCmdBytes === 38 && // expect to have 38 bytes total
            buffer.charCodeAt(i + 5) === 0x50 && // projector names start with P
            buffer.charCodeAt(i + 33) === 0x85;
        let checksum = 0;
        for (let j = 0; j < numCmdBytes - 1; j++) {
            checksum += buffer.charCodeAt(i + j);
        }
        checksum &= 0x00ff;
        let command;
        if (pn502WorkAround) {
            // To support a bug where the product name response isn't a fully formed response,
            // we need to extract the command and throw away the end of the command.
            command = buffer.slice(i, i + numCmdBytes - 6);
            buffer = buffer.slice(i + numCmdBytes);
            return { command, buffer };
        }
        else {
            if (checksum === buffer.charCodeAt(i + numCmdBytes - 1)) {
                command = buffer.slice(i, i + numCmdBytes);
            }
            buffer = buffer.slice(i + numCmdBytes);
            return { command, buffer };
        }
        return undefined;
    },
    isBinary: true,
    sendTerminator: '',
    idleTimeoutMs: 15000,
};
NecProjector.socketOptions = [
    Object.assign(Object.assign({}, NecProjector.baseSocketOptions), { port: 7142 }),
    Object.assign(Object.assign({}, NecProjector.baseSocketOptions), { port: 4999 }),
];
